/**
 * Market Recap — public feed of newsletter-style posts.
 *
 * Route: /market-recap (list) and /market-recap/:slug (single post).
 * Both are anonymous-accessible so pre-signup visitors get proof of an
 * active content cadence before they hand over an email address.
 *
 * List reads from `GET /api/market-recap` (paginated), the single-post
 * view reads from `GET /api/market-recap/:slug`. Admin composer lives
 * separately in the Admin console; users only see the read view here.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Newspaper, CalendarDays, Tag as TagIcon } from "lucide-react";
import usePageSeo from "@/hooks/usePageSeo";

// Same lightweight markdown renderer used by TrustCenter — kept inline so
// we don't pull a full markdown library for this small use-case.
function renderMarkdown(md = "") {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = esc(md).split("\n");
  const out = [];
  let inUl = false, inOl = false;
  const flush = () => { if (inUl) { out.push("</ul>"); inUl = false; } if (inOl) { out.push("</ol>"); inOl = false; } };
  for (const raw of lines) {
    const trimmed = raw.trim();
    if (/^## (.+)/.test(trimmed))     { flush(); out.push(`<h2 class="serif text-2xl mt-6 mb-2">${trimmed.replace(/^## /, "")}</h2>`); continue; }
    if (/^### (.+)/.test(trimmed))    { flush(); out.push(`<h3 class="serif text-xl mt-4 mb-2">${trimmed.replace(/^### /, "")}</h3>`); continue; }
    if (/^- (.+)/.test(trimmed))      { if (!inUl) { flush(); out.push('<ul class="list-disc pl-6 space-y-1 my-2">'); inUl = true; } out.push(`<li>${trimmed.replace(/^- /, "")}</li>`); continue; }
    if (/^\d+\. (.+)/.test(trimmed))  { if (!inOl) { flush(); out.push('<ol class="list-decimal pl-6 space-y-1 my-2">'); inOl = true; } out.push(`<li>${trimmed.replace(/^\d+\. /, "")}</li>`); continue; }
    if (trimmed === "") { flush(); continue; }
    flush();
    out.push(`<p class="leading-relaxed my-2">${trimmed}</p>`);
  }
  flush();
  return out.join("\n")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>")
    .replace(/`([^`]+)`/g, '<code class="mono text-xs bg-muted/60 rounded px-1 py-0.5">$1</code>')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2" class="text-primary underline underline-offset-2">$1</a>');
}

function _fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function MarketRecap() {
  const { slug } = useParams();
  const nav = useNavigate();

  usePageSeo({
    title: slug ? "Market Recap — DPP" : "Market Recap — Dividend Portfolio Planner",
    description: "Weekly dividend market recap and ex-dividend highlights, free to read. Published every Sunday with macro callouts.",
    path: slug ? `/market-recap/${slug}` : "/market-recap",
    ogSlug: "market-recap",
  });

  return slug
    ? <MarketRecapPost slug={slug} onBack={() => nav("/market-recap")} />
    : <MarketRecapList nav={nav} />;
}


function MarketRecapList({ nav }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const PAGE = 12;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/market-recap?limit=${PAGE}&skip=${skip}`);
        if (cancelled) return;
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [skip]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="market-recap-page">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-primary/80 inline-flex items-center gap-2">
          <Newspaper className="w-3.5 h-3.5" /> Weekly · Ad-hoc · Free
        </div>
        <h1 className="serif text-4xl sm:text-5xl mt-3 tracking-tight">Market Recap</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">
          Every Saturday we publish <b>The DPP Weekly</b> — a plain-English summary of what happened in dividend land, plus one Investment 101 lesson. We also drop ad-hoc posts when something big lands mid-week.
        </p>
      </div>

      {loading && items.length === 0 && (
        <div className="text-muted-foreground text-sm" data-testid="market-recap-loading">
          Loading recent recaps…
        </div>
      )}
      {!loading && items.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">No posts yet</div>
            <div className="serif text-2xl mt-2">Check back Saturday</div>
            <p className="text-muted-foreground text-sm mt-2">The Weekly digest posts automatically every Saturday at 8 AM ET.</p>
          </CardContent>
        </Card>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((post) => (
          <Card
            key={post.id}
            className="group cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all border-border hover:border-primary/40"
            data-testid={`market-recap-card-${post.slug}`}
            onClick={() => nav(`/market-recap/${post.slug}`)}
          >
            <CardContent className="p-5">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
                <CalendarDays className="w-3 h-3" /> {_fmtDate(post.published_at)}
                {post.source === "weekly" && (
                  <Badge variant="secondary" className="ml-1 text-[9px] uppercase tracking-widest">Weekly</Badge>
                )}
              </div>
              <h2 className="serif text-lg mt-2 leading-snug group-hover:text-primary transition-colors">{post.title}</h2>
              {post.summary && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{post.summary}</p>
              )}
              {post.tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {post.tags.slice(0, 3).map((t) => (
                    <span key={t} className="mono text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
                      <TagIcon className="w-2.5 h-2.5" /> {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 text-primary text-sm inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Read <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {total > PAGE && (
        <div className="mt-8 flex items-center justify-between">
          <Button
            variant="outline"
            disabled={skip === 0}
            onClick={() => setSkip((s) => Math.max(0, s - PAGE))}
            data-testid="market-recap-prev"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Newer
          </Button>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {skip + 1}–{Math.min(skip + PAGE, total)} of {total}
          </div>
          <Button
            variant="outline"
            disabled={skip + PAGE >= total}
            onClick={() => setSkip((s) => s + PAGE)}
            data-testid="market-recap-next"
          >
            Older <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      )}
    </div>
  );
}


function MarketRecapPost({ slug, onBack }) {
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/market-recap/${slug}`);
        if (!cancelled) setPost(r.data);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <div className="serif text-3xl">Post not found</div>
        <p className="text-muted-foreground mt-2">This recap may have been removed or the link is old.</p>
        <Button className="mt-6" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Market Recap
        </Button>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-6 py-10" data-testid="market-recap-post">
      <button
        onClick={onBack}
        className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1.5 mb-6"
        data-testid="market-recap-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Market Recap
      </button>
      {!post && (
        <div className="text-muted-foreground text-sm">Loading…</div>
      )}
      {post && (
        <>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-2">
            <CalendarDays className="w-3 h-3" /> {_fmtDate(post.published_at)}
            {post.source === "weekly" && (
              <Badge variant="secondary" className="text-[9px] uppercase tracking-widest">Weekly Digest</Badge>
            )}
            <span>· by {post.author}</span>
          </div>
          <h1 className="serif text-4xl sm:text-5xl mt-3 tracking-tight leading-[1.05]">{post.title}</h1>
          {post.summary && <p className="text-lg text-muted-foreground mt-3">{post.summary}</p>}
          <div
            className="prose prose-neutral max-w-none mt-6"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body_md) }}
          />
          {post.tags?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-border flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-muted text-muted-foreground inline-flex items-center gap-1">
                  <TagIcon className="w-2.5 h-2.5" /> {t}
                </span>
              ))}
            </div>
          )}
          <div className="mt-10 rounded-2xl border border-primary/25 bg-primary/[0.04] p-5 text-center">
            <div className="serif text-xl">Get the next recap in your inbox</div>
            <p className="text-sm text-muted-foreground mt-1">The Weekly ships every Saturday at 8 AM ET — free, one-click unsubscribe.</p>
            <Link
              to="/auth?mode=register"
              className="mt-3 inline-flex items-center justify-center rounded-lg h-10 px-5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold transition-colors"
              data-testid="market-recap-subscribe-cta"
            >
              Create Free Account
            </Link>
          </div>
        </>
      )}
    </article>
  );
}
